import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, vShow as _vShow, withDirectives as _withDirectives, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "talk-dashboard__menu" }
const _hoisted_2 = { class: "talk-dashboard__header" }
const _hoisted_3 = { class: "talk-dashboard__actions" }
const _hoisted_4 = {
  role: "dialog",
  "aria-labelledby": "instant_meeting_dialog",
  class: "instant-meeting__dialog",
  "aria-modal": "true"
}
const _hoisted_5 = { class: "talk-dashboard__items" }
const _hoisted_6 = { class: "event-section" }
const _hoisted_7 = { class: "title" }
const _hoisted_8 = { class: "talk-dashboard__event-cards__scroll-indicator" }
const _hoisted_9 = ["src"]
const _hoisted_10 = { class: "talk-dashboard__chats" }
const _hoisted_11 = { class: "talk-dashboard__unread-mentions" }
const _hoisted_12 = ["src"]
const _hoisted_13 = {
  key: 0,
  class: "talk-dashboard__upcoming-reminders"
}
const _hoisted_14 = {
  key: 0,
  class: "upcoming-reminders-list"
}
const _hoisted_15 = ["src"]

import { showError } from '@nextcloud/dialogs'
import { emit } from '@nextcloud/event-bus'
import { isRTL, t } from '@nextcloud/l10n'
import { generateUrl, imagePath } from '@nextcloud/router'
import { useIsMobile, useIsSmallMobile } from '@nextcloud/vue/composables/useIsMobile'
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import NcButton from '@nextcloud/vue/components/NcButton'
import NcInputField from '@nextcloud/vue/components/NcInputField'
import NcPopover from '@nextcloud/vue/components/NcPopover'
import IconArrowLeft from 'vue-material-design-icons/ArrowLeft.vue'
import IconArrowRight from 'vue-material-design-icons/ArrowRight.vue'
import IconCalendarBlankOutline from 'vue-material-design-icons/CalendarBlankOutline.vue'
import IconList from 'vue-material-design-icons/FormatListBulleted.vue'
import IconMicrophoneOutline from 'vue-material-design-icons/MicrophoneOutline.vue'
import IconPhoneOutline from 'vue-material-design-icons/PhoneOutline.vue'
import IconPlus from 'vue-material-design-icons/Plus.vue'
import IconVideoOutline from 'vue-material-design-icons/VideoOutline.vue'
import ConversationsListVirtual from '../LeftSidebar/ConversationsList/ConversationsListVirtual.vue'
import SearchMessageItem from '../RightSidebar/SearchMessages/SearchMessageItem.vue'
import LoadingPlaceholder from '../UIShared/LoadingPlaceholder.vue'
import DashboardSection from './DashboardSection.vue'
import EventCard from './EventCard.vue'
import { CONVERSATION } from '../../constants.ts'
import { getTalkConfig, hasTalkFeature } from '../../services/CapabilitiesManager.ts'
import { EventBus } from '../../services/EventBus.ts'
import { useActorStore } from '../../stores/actor.ts'
import { useDashboardStore } from '../../stores/dashboard.ts'
import { hasUnreadMentions } from '../../utils/conversation.ts'
import { copyConversationLinkToClipboard } from '../../utils/handleUrl.ts'


export default /*@__PURE__*/_defineComponent({
  __name: 'TalkDashboard',
  setup(__props) {

const supportsUpcomingReminders = hasTalkFeature('local', 'upcoming-reminders')
const canModerateSipDialOut = hasTalkFeature('local', 'sip-support-dialout')
	&& getTalkConfig('local', 'call', 'sip-enabled')
	&& getTalkConfig('local', 'call', 'sip-dialout-enabled')
	&& getTalkConfig('local', 'call', 'can-enable-sip')
const canStartConversations = getTalkConfig('local', 'conversations', 'can-create')
const isDirectionRTL = isRTL()
const isMobile = useIsMobile()
const isSmallMobile = useIsSmallMobile()

const store = useStore()
const router = useRouter()
const dashboardStore = useDashboardStore()
const actorStore = useActorStore()
const forwardScrollable = ref(false)
const backwardScrollable = ref(false)
const eventCardsWrapper = ref<HTMLDivElement | null>(null)
const eventRooms = computed(() => dashboardStore.eventRooms || [])
const upcomingReminders = computed(() => dashboardStore.upcomingReminders || [])
const eventsInitialised = computed(() => dashboardStore.eventRoomsInitialised)
const remindersInitialised = computed(() => dashboardStore.upcomingRemindersInitialised)
const conversationName = ref('')
let actualizeDataInterval: ReturnType<typeof setInterval> | null = null

// Data fetching handlers

/**
 * Fetches all necessary data for the dashboard.
 */
async function actualizeData() {
	await Promise.all([
		dashboardStore.fetchDashboardEventRooms(),
		dashboardStore.fetchUpcomingReminders(),
	])
}

/**
 * Initializes the data fetching interval and fetches initial data.
 */
function initActualizeData() {
	if (actualizeDataInterval) {
		clearInterval(actualizeDataInterval)
	}
	actualizeData()
	actualizeDataInterval = setInterval(actualizeData, 300_000)
}

initActualizeData()
EventBus.on('refresh-talk-dashboard', initActualizeData)

onBeforeUnmount(() => {
	if (actualizeDataInterval) {
		clearInterval(actualizeDataInterval)
	}

	if (eventCardsWrapper?.value) {
		resizeObserver.disconnect()
	}

	EventBus.off('refresh-talk-dashboard', initActualizeData)
})

watch(eventCardsWrapper, (newValue) => {
	if (newValue) {
		resizeObserver.observe(newValue)
	}
})

/**
 * Updates the scrollable flags based on the current scroll position.
 */
async function updateScrollableFlags() {
	await nextTick()
	if (eventCardsWrapper.value) {
		const { scrollLeft, scrollWidth, clientWidth } = eventCardsWrapper.value
		backwardScrollable.value = isDirectionRTL ? scrollLeft < 0 : scrollLeft > 0
		forwardScrollable.value = (isDirectionRTL ? -1 : 1) * scrollLeft + clientWidth < scrollWidth - 10 // 10px tolerance
	}
}

// Use ResizeObserver to detect size changes
const resizeObserver = new ResizeObserver(() => {
	updateScrollableFlags()
})

const conversationsInitialised = computed(() => store.getters.conversationsInitialised)
const filteredConversations = computed(() => store.getters.conversationsList.filter(hasUnreadMentions))

/**
 * Creates a new group conversation and navigates to the conversation page.
 */
async function startMeeting() {
	try {
		const conversation = await store.dispatch('createGroupConversation', {
			// TRANSLATORS: Section header for meeting-related settings; also a static name fallback for instant meeting conversation
			roomName: conversationName.value || t('spreed', 'Meeting'),
			roomType: CONVERSATION.TYPE.PUBLIC,
			objectType: CONVERSATION.OBJECT_TYPE.INSTANT_MEETING,
			objectId: Math.floor(Date.now() / 1000).toString(),
		})
		await copyConversationLinkToClipboard(conversation.token)
		await router.push({
			name: 'conversation',
			params: { token: conversation.token },
			hash: '#direct-call',
		})
	} catch (error) {
		console.error('Error creating conversation:', error)
		showError(t('spreed', 'Error while creating the conversation'))
	}
}

/**
 * Scrolls the event cards wrapper in the specified direction.
 *
 * @param payload
 * @param payload.direction - The direction to scroll ('backward' or 'forward').
 */
function scrollEventCards({ direction }: { direction: 'backward' | 'forward' }) {
	const scrollDirection = (direction === 'backward' ? -1 : 1) * (isDirectionRTL ? -1 : 1)
	if (eventCardsWrapper.value) {
		const ITEM_WIDTH = 300 + 8 // 300px width + 8px gap
		let scrollAmount = 0
		const visibleItems = Math.floor(eventCardsWrapper.value.clientWidth / ITEM_WIDTH)
		if (visibleItems === 0) {
			scrollAmount = eventCardsWrapper.value.clientWidth * scrollDirection
		} else {
			scrollAmount = visibleItems * ITEM_WIDTH * scrollDirection
			// Arrow buttons are 34px wide
			if (!backwardScrollable.value && scrollDirection === 1) {
				scrollAmount -= 34
			} else if (!forwardScrollable.value && scrollDirection === -1) {
				scrollAmount += 34
			}
		}

		eventCardsWrapper.value.scrollBy({
			left: scrollAmount,
			behavior: 'smooth',
		})
	}
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["talk-dashboard-wrapper", {
			'talk-dashboard-wrapper--mobile': _unref(isMobile),
			'talk-dashboard-wrapper--small-mobile': _unref(isSmallMobile),
		}])
  }, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("h2", _hoisted_2, _toDisplayString(_unref(t)('spreed', 'Hello, {displayName}', { displayName: _unref(actorStore).displayName }, { escape: false })), 1),
      _createElementVNode("div", _hoisted_3, [
        (_unref(canStartConversations))
          ? (_openBlock(), _createBlock(_unref(NcPopover), {
              key: 0,
              "popup-role": "dialog"
            }, {
              trigger: _withCtx(() => [
                _createVNode(_unref(NcButton), { variant: "primary" }, {
                  icon: _withCtx(() => [
                    _createVNode(IconVideoOutline)
                  ]),
                  default: _withCtx(() => [
                    _createTextVNode(" " + _toDisplayString(_unref(t)('spreed', 'Start meeting now')), 1)
                  ]),
                  _: 1
                })
              ]),
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_4, [
                  _createElementVNode("strong", null, _toDisplayString(_unref(t)('spreed', 'Give your meeting a title')), 1),
                  _createVNode(_unref(NcInputField), {
                    id: "room-name",
                    modelValue: conversationName.value,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((conversationName).value = $event)),
                    placeholder: _unref(t)('spreed', 'Meeting')
                  }, null, 8, ["modelValue", "placeholder"]),
                  _createVNode(_unref(NcButton), {
                    variant: "primary",
                    onClick: startMeeting
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_unref(t)('spreed', 'Create and copy link')), 1)
                    ]),
                    _: 1
                  })
                ])
              ]),
              _: 1
            }))
          : _createCommentVNode("", true),
        (_unref(canStartConversations))
          ? (_openBlock(), _createBlock(_unref(NcButton), {
              key: 1,
              onClick: _cache[1] || (_cache[1] = ($event: any) => (_unref(EventBus).emit('new-conversation-dialog:show')))
            }, {
              icon: _withCtx(() => [
                _createVNode(IconPlus, { size: 20 })
              ]),
              default: _withCtx(() => [
                _createTextVNode(" " + _toDisplayString(_unref(t)('spreed', 'Create a new conversation')), 1)
              ]),
              _: 1
            }))
          : _createCommentVNode("", true),
        _createVNode(_unref(NcButton), {
          onClick: _cache[2] || (_cache[2] = ($event: any) => (_unref(EventBus).emit('open-conversations-list:show')))
        }, {
          icon: _withCtx(() => [
            _createVNode(IconList, { size: 20 })
          ]),
          default: _withCtx(() => [
            _createTextVNode(" " + _toDisplayString(_unref(t)('spreed', 'Join open conversations')), 1)
          ]),
          _: 1
        }),
        (_unref(canModerateSipDialOut))
          ? (_openBlock(), _createBlock(_unref(NcButton), {
              key: 2,
              onClick: _cache[3] || (_cache[3] = ($event: any) => (_unref(EventBus).emit('call-phone-dialog:show')))
            }, {
              icon: _withCtx(() => [
                _createVNode(IconPhoneOutline, { size: 20 })
              ]),
              default: _withCtx(() => [
                _createTextVNode(" " + _toDisplayString(_unref(t)('spreed', 'Call a phone number')), 1)
              ]),
              _: 1
            }))
          : _createCommentVNode("", true),
        _createVNode(_unref(NcButton), {
          variant: "secondary",
          onClick: _cache[4] || (_cache[4] = ($event: any) => (_unref(emit)('talk:media-settings:show', 'device-check')))
        }, {
          icon: _withCtx(() => [
            _createVNode(IconMicrophoneOutline, { size: 20 })
          ]),
          default: _withCtx(() => [
            _createTextVNode(" " + _toDisplayString(_unref(t)('spreed', 'Check devices')), 1)
          ]),
          _: 1
        })
      ])
    ]),
    _createElementVNode("div", _hoisted_5, [
      _createElementVNode("div", _hoisted_6, [
        (eventsInitialised.value && eventRooms.value.length > 0)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              _createElementVNode("h3", _hoisted_7, _toDisplayString(_unref(t)('spreed', 'Upcoming meetings')), 1),
              _createElementVNode("div", {
                class: _normalizeClass(["talk-dashboard__event-cards-wrapper", { 'forward-scrollable': forwardScrollable.value, 'backward-scrollable': backwardScrollable.value }])
              }, [
                _createElementVNode("div", {
                  ref_key: "eventCardsWrapper",
                  ref: eventCardsWrapper,
                  class: "talk-dashboard__event-cards",
                  onScrollPassive: updateScrollableFlags
                }, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(eventRooms.value, (eventRoom) => {
                    return (_openBlock(), _createBlock(EventCard, {
                      key: eventRoom.eventLink,
                      "event-room": eventRoom,
                      class: "talk-dashboard__event-card"
                    }, null, 8, ["event-room"]))
                  }), 128))
                ], 544),
                _createElementVNode("div", _hoisted_8, [
                  _withDirectives(_createVNode(_unref(NcButton), {
                    class: "button-slide backward",
                    variant: "tertiary",
                    title: _unref(t)('spreed', 'Scroll backward'),
                    "aria-label": _unref(t)('spreed', 'Scroll backward'),
                    onClick: _cache[5] || (_cache[5] = ($event: any) => (scrollEventCards({ direction: 'backward' })))
                  }, {
                    icon: _withCtx(() => [
                      _createVNode(IconArrowLeft, { class: "bidirectional-icon" })
                    ]),
                    _: 1
                  }, 8, ["title", "aria-label"]), [
                    [_vShow, backwardScrollable.value]
                  ]),
                  _withDirectives(_createVNode(_unref(NcButton), {
                    class: "button-slide forward",
                    variant: "tertiary",
                    title: _unref(t)('spreed', 'Scroll forward'),
                    "aria-label": _unref(t)('spreed', 'Scroll forward'),
                    onClick: _cache[6] || (_cache[6] = ($event: any) => (scrollEventCards({ direction: 'forward' })))
                  }, {
                    icon: _withCtx(() => [
                      _createVNode(IconArrowRight, { class: "bidirectional-icon" })
                    ]),
                    _: 1
                  }, 8, ["title", "aria-label"]), [
                    [_vShow, forwardScrollable.value]
                  ])
                ])
              ], 2)
            ], 64))
          : (!eventsInitialised.value)
            ? (_openBlock(), _createBlock(LoadingPlaceholder, {
                key: 1,
                type: "event-cards"
              }))
            : (_openBlock(), _createBlock(DashboardSection, {
                key: 2,
                class: "event-section--empty",
                wide: "",
                title: _unref(t)('spreed', 'Schedule meetings'),
                subtitle: _unref(t)('spreed', 'You don\'t have any upcoming meetings'),
                description: _unref(t)('spreed', 'Schedule a meeting from your calendar. A Talk conversation needs to be set as location to show up here')
              }, {
                image: _withCtx(() => [
                  _createElementVNode("img", {
                    src: _unref(imagePath)('spreed', 'dashboard/meetings.png')
                  }, null, 8, _hoisted_9)
                ]),
                action: _withCtx(() => [
                  _createVNode(_unref(NcButton), {
                    variant: "secondary",
                    href: _unref(generateUrl)('apps/calendar'),
                    target: "_blank"
                  }, {
                    icon: _withCtx(() => [
                      _createVNode(IconCalendarBlankOutline, { size: 20 })
                    ]),
                    default: _withCtx(() => [
                      _createTextVNode(" " + _toDisplayString(_unref(t)('spreed', 'Open calendar')), 1)
                    ]),
                    _: 1
                  }, 8, ["href"])
                ]),
                _: 1
              }, 8, ["title", "subtitle", "description"]))
      ]),
      _createElementVNode("div", _hoisted_10, [
        _createElementVNode("div", _hoisted_11, [
          (filteredConversations.value.length > 0 || !conversationsInitialised.value)
            ? (_openBlock(), _createBlock(DashboardSection, {
                key: 0,
                title: _unref(t)('spreed', 'Unread mentions')
              }, {
                list: _withCtx(() => [
                  _createVNode(ConversationsListVirtual, {
                    class: "talk-dashboard__conversations-list",
                    conversations: filteredConversations.value,
                    loading: !conversationsInitialised.value
                  }, null, 8, ["conversations", "loading"])
                ]),
                _: 1
              }, 8, ["title"]))
            : (_openBlock(), _createBlock(DashboardSection, {
                key: 1,
                title: _unref(t)('spreed', 'Unread mentions'),
                description: _unref(t)('spreed', 'Messages where you were mentioned will show up here. You can mention people by typing @ followed by their name')
              }, {
                image: _withCtx(() => [
                  _createElementVNode("img", {
                    src: _unref(imagePath)('spreed', 'dashboard/mentions.png')
                  }, null, 8, _hoisted_12)
                ]),
                _: 1
              }, 8, ["title", "description"]))
        ]),
        (_unref(supportsUpcomingReminders))
          ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
              (upcomingReminders.value.length > 0 || !remindersInitialised.value)
                ? (_openBlock(), _createBlock(DashboardSection, {
                    key: 0,
                    title: _unref(t)('spreed', 'Upcoming reminders')
                  }, {
                    list: _withCtx(() => [
                      (remindersInitialised.value)
                        ? (_openBlock(), _createElementBlock("ul", _hoisted_14, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(upcomingReminders.value, (reminder) => {
                              return (_openBlock(), _createBlock(SearchMessageItem, {
                                key: reminder.messageId,
                                "message-id": reminder.messageId,
                                title: reminder.actorDisplayName,
                                subline: reminder.message,
                                "message-parameters": reminder.messageParameters,
                                token: reminder.roomToken,
                                to: {
										name: 'conversation',
										params: { token: reminder.roomToken },
										hash: `#message_${reminder.messageId}`,
									},
                                "actor-id": reminder.actorId,
                                "actor-type": reminder.actorType,
                                timestamp: reminder.reminderTimestamp,
                                "is-reminder": ""
                              }, null, 8, ["message-id", "title", "subline", "message-parameters", "token", "to", "actor-id", "actor-type", "timestamp"]))
                            }), 128))
                          ]))
                        : (_openBlock(), _createBlock(LoadingPlaceholder, {
                            key: 1,
                            class: "upcoming-reminders__loading-placeholder",
                            type: "conversations"
                          }))
                    ]),
                    _: 1
                  }, 8, ["title"]))
                : (_openBlock(), _createBlock(DashboardSection, {
                    key: 1,
                    title: _unref(t)('spreed', 'Message reminders'),
                    description: _unref(t)('spreed', 'Set a reminder on a message to be notified')
                  }, {
                    image: _withCtx(() => [
                      _createElementVNode("img", {
                        src: _unref(imagePath)('spreed', 'dashboard/reminders.png')
                      }, null, 8, _hoisted_15)
                    ]),
                    _: 1
                  }, 8, ["title", "description"]))
            ]))
          : _createCommentVNode("", true)
      ])
    ])
  ], 2))
}
}

})